import { MutableRefObject, useEffect, useMemo, useRef } from 'react'

import type { GridLayoutDefinition } from '@livekit/components-core'

import { GRID_LAYOUTS, selectGridLayout } from '@/src/lib/grid-layouts'
import { useSize } from '@/src/hooks/internal'

/**
 * The `useGridLayout` hook tries to select the best layout to fit all tiles.
 * If the available screen space is not enough, it will reduce the number of maximum visible
 * tiles and select a layout that still works visually within the given limitations.
 * As the order of tiles changes over time, the hook tries to keep visual updates to a minimum
 * while trying to display important tiles such as speaking participants or screen shares.
 *
 * @example
 * ```tsx
 * const { layout } = useGridLayout(gridElement, trackCount);
 * ```
 * @public
 */
export function useGridLayout(
  /** HTML element that contains the grid. */
  gridElement: MutableRefObject<HTMLDivElement>,
  /** Count of tracks that should get layed out */
  trackCount: number,
  isPiP?: boolean,
): { layout: GridLayoutDefinition } {
  const { width, height } = useSize(gridElement, isPiP)
  // Store the last valid layout to prevent flickering during resize/zoom
  const lastValidLayoutRef = useRef<GridLayoutDefinition | null>(null)

  const layout = useMemo(() => {
    if (isPiP) {
      if (trackCount > 1) {
        return GRID_LAYOUTS[3]
      } else {
        return GRID_LAYOUTS[2]
      }
    }

    if (width > 0 && height > 0) {
      const newLayout = selectGridLayout(GRID_LAYOUTS, trackCount, width, height)
      lastValidLayoutRef.current = newLayout
      return newLayout
    } else {
      // During zoom/resize transitions, dimensions can temporarily become 0
      // Keep the last valid layout instead of falling back to 1x1
      return lastValidLayoutRef.current ?? GRID_LAYOUTS[2]
    }
  }, [isPiP, width, height, trackCount])

  useEffect(() => {
    if (gridElement.current && layout) {
      gridElement.current.style.setProperty('--lk-col-count', layout?.columns.toString())
      gridElement.current.style.setProperty('--lk-row-count', layout?.rows.toString())
    }
  }, [gridElement, layout])

  return {
    layout,
  }
}
